import { CommonModule } from "@angular/common";
import { Component, EventEmitter, Input, Output, ElementRef, ViewChild } from "@angular/core";
import { ChevronDown, LucideAngularModule } from "lucide-angular";

export type SimpleSelectOption = {
    value: unknown;
    label: unknown;
    disabled?: boolean;
};

@Component({
    selector: "app-simple-select",
    standalone: true,
    imports: [ CommonModule, LucideAngularModule ],
    templateUrl: "./simple.select.component.html",
    styleUrls: ["./simple.select.component.scss"]
  })
  export class SimpleSelectComponent {
    @Input() options: SimpleSelectOption[] | null = [];
    @Input() selectedOption: unknown = null;
    @Output() selectedOptionChange = new EventEmitter<unknown>();
    @Input() label?: string;
    @Input() ariaLabel?: string;
    @Input() noBorder: boolean = false;
    @ViewChild("selectElement") selectElement?: ElementRef<HTMLSelectElement>;

    protected chevronDownIcon = ChevronDown;

    onSelect(event: unknown) {
        const target = (event as Event).target as HTMLSelectElement;
        this.selectedOption = target.value;
        this.selectedOptionChange.emit(this.selectedOption);
    }

    protected matchSelectedOption(option: SimpleSelectOption): boolean {
        return this.selectedOption === option.value;
    }

    onIconClick() {
        // Use showPicker() method to open the select dropdown
        if (this.selectElement?.nativeElement) {
            const selectEl = this.selectElement.nativeElement;
            
            // Try using showPicker() method (modern browsers)
            if ("showPicker" in selectEl && typeof selectEl.showPicker === "function") {
                try {
                    selectEl.showPicker();
                } catch (error) {
                    // Fallback: just focus the select
                    selectEl.focus();
                }
            } else {
                // Fallback for older browsers
                selectEl.focus();
                
                // Try to simulate mouse events to open the select
                const mouseEvent = new MouseEvent("mousedown", {
                    bubbles: true,
                    cancelable: true,
                    view: window
                });
                selectEl.dispatchEvent(mouseEvent);
            }
        }
    }

    get value() {
        return this.selectedOption;
    }

    get effectiveAriaLabel(): string | undefined {
        if (this.ariaLabel) {
            return this.ariaLabel;
        }
        if (!this.label) {
            return "Select an option"; // Default aria-label when no visible label
        }
        return undefined; // Use the visible label
    }

  }